import { GO } from './common';
import { CATALOGUE_CATEGORY, CATALOGUE_TYPE, CATALOGUE_SRC } from './options';

export enum InsuEnum {
  /** - 1 - areaName */
  AN = 'area_name',
  /** - 2 - areaMode: polygon | default */
  AT = 'area_type',
  /** - 3 - category */
  SC = 'i_serve_category',
  /** - 4 - area(expose) type (exposed | not_exposed) */
  IX = 'i_expose_type',
  /** - 5 - insulationType */
  IT = 'i_insulation_type',
  /** - 6 - insulationThickness */
  IC = 'i_thickness',
  /** - 7 - claddingType */
  CT = 'i_cladding_type',
}

export type InsulationAreaSetting = {
  /** Polygon Area or Default Area */
  areaMode?: 'polygon' | 'default';
  /** Polygon Area name - @2026/04/15 */
  areaName: string;
  /** insulation serve category */
  serveCategory: 'DUCT' | 'PIPE';
  /**
   * insulation area type (expose type)
   * `none` means no-insulations in the area
   */
  exposeType: string;
  // ====== system level properties (per-symbol sub-panel still uses these) ========
  /** Insulation thickness in inches */
  insulationThickness?: string;
  /** Insulation type, aka materials */
  insulationType?: string;
  /** Cladding type */
  claddingType?: string;
  /** area name prefix, e.g. "ROOM" → areas named ROOM-1, ROOM-2 ... */
  namePrefix?: string;
  /** include: length_1 & length_unit_str  */
  segment?: GO;
  /** insulation groupsby system -  a map strucutre */
  property_groups?: GO;
};

/**
 * === IN USE ===
 */
export type EntityGroup = { [entity: string]: GO[] };

/**
 * === IN USE ===
 * Original data structure from API.
 * after got this, data conversion will be like this:
 * TKJobItem --> tranformer --> BPSymbol --> BPMapEditor --> Features
 */
export interface TKJobItem {
  detection_mode: string;
  index: number;
  item: {
    /** FITTING | LENGTH, highest level symbol classification */
    type: string;
    /** DUCT | PIPE, second level symbol classification */
    category: string;
    /** item type in snake capitalized, eg. ELBOW, third level symbol classification */
    id: string;
    description: string;
    s3_icon_path: string | null;
  };
  job_id: string;
  /** consider length properties @2023/03/24 */
  coordinates: LengthSegment[];
  properties: FieldPair[];
  s3_cropped_image_path: string;
  /** if user validated this symbol manually, 1|0 - @2023/10/25 */
  user_validated: number;
}

/**
 * === IN USE ===
 */
export interface TKItemCatalog {
  /** LENGTH, FITTING ... */
  type: CATALOGUE_TYPE;
  /** PIPE | DUCT | PLUMBING */
  category: string;
  description: string;
  /** actual sybmol type in capital format */
  id: string;
  s3_icon_path: string;
  properties: TKItemProperty[];
  /** control item visibility @2025/03/13 */
  ui_visible?: boolean;
  /** item belongs to where @2025/03/13 */
  classification_type?: CATALOGUE_SRC;
}

/**
 * === IN USE ===
 * for `Symbol properties` panel use, to check detail of selected fitting
 */
export type FittingMeta = {
  /**
   * FIRST LEVEL: FITTING | LENGTH,
   * aka, `type` from backend item-catalogue defintion!
   */
  rawType?: string;
  /** SECONDE LEVEL: PIPE | DUCT | PLUMBING */
  category: string;
  /** capitalized symbol type, like ELBOW */
  type: string;
  /** symbol description */
  label: string;
  /** remote icon */
  icon: string;
  /** mark it could have different shape and allow to change - @2025/06/11  */
  shape?: true;
  /** item properties but no system, no shape */
  fields: string[];
  /** raw properties name from catalogue */
  props: string[];
  /** system property defined */
  hasSysem?: boolean;
  /** raw properties object from catalogue */
  properties: TKItemProperty[];
  /** if CUSTOM or STANDARD - @2025/05/27 */
  isCustom?: boolean;
  /** visible in dropdown menu - @2026/02/20 */
  uiVisible?: boolean;
};

/**
 * === IN USE ===
 * FIXME: backend changed this schema ... so front-end no longer working...
 * @2023/07/26
 */
export interface TKItemProperty extends TKItemPropertyRange {
  /** property name, aka `filed` name */
  name: string;
  data_type: string | number;
  /** for display use at side panel @2023/06/28 */
  label: string;
  constant_values: TKItemConstantValue[];
  date_created: string;
  date_modified: string;
  last_modified_by: string | null;
  created_by: string | null;
}
/**
 * === IN USE ===
 */
export interface TKItemConstantValue {
  acronym: string | null;
  category: string;
  color_code: string | null;
  /** FIXME: this an object list? */
  constant_value: string;
  description: string;
  position: number;
  property_name: string;
}

/**
 * === IN USE ===
 */
export interface TKItemPropertyRange {
  imperial_upper_bound: number | null;
  imperial_lower_bound: number | null;
  metric_upper_bound: number | null;
  metric_lower_bound: number | null;
}

/**
 * === IN USE ===
 */
export type LengthSegment = SymbolSegment & LengthMeasure;

/**
 * === IN USE ===
 */
export type LengthMeasure = {
  length_mm: number;
  length_pixels: number;
  length_scaled_mm: number;
  length_unit?: number;
  length_unit_str?: string;
  scale: string;
};

/**
 * === IN USE ===
 */
export interface TKSystemCatalog {
  acronym: string;
  category: CATALOGUE_CATEGORY;
  color_code: string;
  /** unique key */
  constant_value: string;
  description: string;
  property_name: string;
}

export type NIDSymbolEntity = Omit<FittingEntity, 'id' | 'ukey'>;

/**
 * Strong typed of all properties that may included in symbol details
 *
 * === IN USE ===
 *
 * `detail` of feature and BPSymbol.
 *
 * Some properties(system, entityType, category, icon) are set from:
 *
 *  function `symbolDetailEnhancer`
 */
export type FittingEntity = {
  /**
   * nanoid, a must to have property, available while symbol selected to correct.
   * only ONE case no id:
   *  - drawing new symbol
   */
  id: string;
  /** symbol unique key - @2026/03/02 */
  ukey: string;
  system: string;
  /** Fitting type of symbol, like `ELBOW` */
  entityType: string;
  /** DUCT | PIPE */
  category: string;
  /** must have prop! */
  icon: string;
  /** tool type, used for ?? */
  toolType?: string;
  /** FITTING | LENGTH - @2024/11/23 */
  itemType: string;
} & FittingDimension;

export type FittingDimension = RectDimension &
  EquipmentDimension &
  LengthDimension &
  CommonDimension;

type CommonDimension = {
  shape?: string;
  angle?: string;
  diameter?: string;
  diameter_1?: string;
  diameter_2?: string;
  /** job item height from AI */
  height_1?: string;
  /** job item width from AI */
  width_1?: string;
  width_2?: string;
  /** job item system from AI */
  system?: string;
  /** no way to detect it from AI */
  'diffuser-code'?: string;
  /** seems useless - @2023/07/27 */
  w1?: string;
  w2?: string;
  w3?: string;
  zone?: string;
};

/**
 * === IN USE ===
 */
type EquipmentDimension = {
  /** added for equipment correction @2023/05/25 */
  name?: string;
  /** added for equipment correction @2023/05/25 */
  subtype?: string;
  /** equipment number after subtype: name-subtype-n */
  s_number?: string;
  /** identifier field: @2024/12/10 */
  identifier?: string;
  /** multiplier @2025/10/17 */
  multiplier?: string;
};
/**
 * === IN USE ===
 */
type RectDimension = {
  /** === symbol height in pixels for rectangle */
  height?: number;
  /** === symbol width in pixels for rectangle */
  width?: number;
};
/**
 * === IN USE ===
 */
type LengthDimension = {
  /** generated on the fly while user resizing line from `generateLineSymbolWith` */
  length?: string;
  /** ===added in length drawing @2023/03/29 */
  length_pixels?: number;
  /** pipe length in pixel */
  length_1?: string;
  /** real length in ft or m */
  length_unit_str?: string;
};
/**
 * === IN USE ===
 */
export type SymbolSegment = {
  segment: number;
  x_1: number;
  y_1: number;
  x_2: number;
  y_2: number;
};
/**
 * === IN USE ===
 */
export type FieldPair = {
  name: string;
  value: string | number;
};

/**
 * === IN USE ===
 * system definition of drawing
 */
export type TKSystem = {
  /** actual value in use by symbol */
  short: string;
  fullName: string;
  color: string;
  /** value of DUCT|PIPE currently, one of CATALOGUE_CATEGORY */
  category: string;
};

export type SymbolChild = {
  item_id: string;
  index: number;
};
